import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createGamificationService } from '../services/gamificationService';
import { useAuth } from '../../auth/context/AuthContext';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import type { FortressStatus, Quest, GameTask, ComboEvent } from '../types';

export const gameKeys = {
  status: () => ['game', 'status'] as const,
  quests: (customerId?: string) => ['game', 'quests', customerId ?? null] as const,
  profile: () => ['game', 'profile'] as const,
};

interface GameData {
  fortressStatus: FortressStatus | null;
  quests: Quest[];
  pendingCombo: ComboEvent | null;
  loading: boolean;
}

const GameDataContext = createContext<GameData | undefined>(undefined);

export const useGameData = (): GameData => {
  const ctx = useContext(GameDataContext);
  if (!ctx) throw new Error('useGameData must be used within GameProvider');
  return ctx;
};

interface GameActions {
  loadStatus: () => Promise<void>;
  loadQuests: (customerId?: string) => Promise<void>;
  claimTask: (questId: string, taskId: string) => Promise<GameTask>;
  completeTask: (questId: string, taskId: string) => Promise<GameTask>;
  clearCombo: () => void;
  dispatchCombo: (event: ComboEvent) => void;
}

const GameActionsContext = createContext<GameActions | undefined>(undefined);

export const useGameActions = (): GameActions => {
  const ctx = useContext(GameActionsContext);
  if (!ctx) throw new Error('useGameActions must be used within GameProvider');
  return ctx;
};

export const useGame = () => {
  const data = useGameData();
  const actions = useGameActions();
  return { ...data, ...actions };
};

export const GameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // GameProvider is always mounted (GamificationLayer), the login screen included, so it holds a
  // service only for a signed-in user and calls the game backend only while the switch is on.
  const { idToken } = useAuth();
  const service = useMemo(() => (idToken ? createGamificationService(idToken) : null), [idToken]);
  const queryClient = useQueryClient();
  const switchOn = useFeatureSwitch('gamification');
  const gamificationOn = switchOn && service !== null;
  const signedIn = () => {
    if (!service) throw new Error('Not signed in');
    if (!switchOn) throw new Error('Gamification is switched off');
    return service;
  };

  const [pendingCombo, setPendingCombo] = useState<ComboEvent | null>(null);
  // Quests load once a consumer (QuestPanel) asks, and follow the customer it asks for.
  const [questCustomerId, setQuestCustomerId] = useState<string | undefined>(undefined);
  const [questsRequested, setQuestsRequested] = useState(false);

  const statusQuery = useQuery({
    queryKey: gameKeys.status(),
    queryFn: () => signedIn().getStatus(),
    enabled: gamificationOn,
  });

  const questsQuery = useQuery({
    queryKey: gameKeys.quests(questCustomerId),
    queryFn: () => signedIn().listQuests(questCustomerId),
    enabled: gamificationOn && questsRequested,
    // Keep the prior customer's quests visible while switching, no empty flash.
    placeholderData: keepPreviousData,
  });

  const fortressStatus = statusQuery.data ?? null;
  const quests = useMemo(
    () => (Array.isArray(questsQuery.data) ? questsQuery.data : []),
    [questsQuery.data],
  );

  const loadStatus = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: gameKeys.status() });
  }, [queryClient]);

  const loadQuests = useCallback(async (customerId?: string) => {
    setQuestCustomerId(customerId);
    setQuestsRequested(true);
    await queryClient.invalidateQueries({ queryKey: gameKeys.quests(customerId) });
  }, [queryClient]);

  const claimMutation = useMutation({
    mutationFn: ({ questId, taskId }: { questId: string; taskId: string }) => signedIn().claimTask(questId, taskId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: gameKeys.status() }),
  });

  const completeMutation = useMutation({
    mutationFn: ({ questId, taskId }: { questId: string; taskId: string }) => signedIn().completeTask(questId, taskId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: gameKeys.status() }),
  });

  // Depend on the stable mutateAsync fn, not the mutation result object (a new
  // reference each render) — otherwise the actions context churns every render.
  const { mutateAsync: claimAsync } = claimMutation;
  const { mutateAsync: completeAsync } = completeMutation;
  const claimTask = useCallback(
    (questId: string, taskId: string) => claimAsync({ questId, taskId }),
    [claimAsync],
  );
  const completeTask = useCallback(
    (questId: string, taskId: string) => completeAsync({ questId, taskId }),
    [completeAsync],
  );

  const clearCombo = useCallback(() => setPendingCombo(null), []);
  const dispatchCombo = useCallback((event: ComboEvent) => setPendingCombo(event), []);

  const dataValue = useMemo<GameData>(() => ({
    fortressStatus,
    quests,
    pendingCombo,
    // loading tracks the fortress status read.
    loading: statusQuery.isFetching,
  }), [fortressStatus, quests, pendingCombo, statusQuery.isFetching]);

  const actionsValue = useMemo<GameActions>(() => ({
    loadStatus,
    loadQuests,
    claimTask,
    completeTask,
    clearCombo,
    dispatchCombo,
  }), [loadStatus, loadQuests, claimTask, completeTask, clearCombo, dispatchCombo]);

  return (
    <GameDataContext.Provider value={dataValue}>
      <GameActionsContext.Provider value={actionsValue}>
        {children}
      </GameActionsContext.Provider>
    </GameDataContext.Provider>
  );
};
